import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton
} from '@mui/material';
import {
  Add, Edit, Delete, Search, CheckCircle, Close, Flag, Assignment
} from '@mui/icons-material';
import { tasksAPI, usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';

const empty = { title: '', description: '', priority: 'medium', status: 'pending', assignedTo: '', dueDate: '' };

const priorityMap = {
  low: { color: '#10b981', icon: <Flag sx={{ fontSize: 14 }} /> },
  medium: { color: '#f59e0b', icon: <Flag sx={{ fontSize: 14 }} /> },
  high: { color: '#ef4444', icon: <Flag sx={{ fontSize: 14 }} /> },
  urgent: { color: '#dc2626', icon: <Flag sx={{ fontSize: 14 }} /> }
};
const statusMap = {
  pending: { bg: '#f1f5f9', color: '#64748b' },
  in_progress: { bg: alpha('#3b82f6', 0.1), color: '#2563eb' },
  completed: { bg: alpha('#10b981', 0.1), color: '#059669' },
  cancelled: { bg: alpha('#ef4444', 0.1), color: '#dc2626' }
};

const Tasks = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, u] = await Promise.all([tasksAPI.getAll({ limit: 1000 }), usersAPI.getAll()]);
      setRows(Array.isArray(t.data) ? t.data : t.data?.data || []); setUsers(Array.isArray(u.data) ? u.data : u.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Tasks | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r =>
    [r.title, r.description].some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
  );

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      setForm({
        title: row.title || '', description: row.description || '',
        priority: row.priority || 'medium', status: row.status || 'pending',
        assignedTo: row.assignedTo?._id || row.assignedTo || '',
        dueDate: row.dueDate ? row.dueDate.slice(0, 10) : ''
      });
    } else { setEditing(null); setForm(empty); }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const d = { ...form }; if (!d.assignedTo) delete d.assignedTo;
      if (editing) await tasksAPI.update(editing, d);
      else await tasksAPI.create(d);
      setOpen(false); load();
      toast.success(editing ? 'Task updated successfully' : 'Task created successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async () => {
    try { await tasksAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Task deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const F = (f, v) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Tasks</Typography>
          <Typography variant="body2" color="text.secondary">Track and manage team tasks</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
          sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 4px 14px rgba(16,185,129,0.3)', px: 3, fontWeight: 700 }}>
          New Task
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <TextField size="small" placeholder="Search tasks..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned To</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                          <CheckCircle sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No tasks found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => {
                        const pm = priorityMap[r.priority] || priorityMap.medium;
                        const sm = statusMap[r.status] || statusMap.pending;
                        return (
                          <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#10b981', 0.03) } }}>
                            <TableCell>
                              <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.title}</Typography>
                                {r.description && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', maxWidth: 300 }} noWrap>{r.description}</Typography>}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip icon={pm.icon} label={r.priority} size="small"
                                sx={{ fontWeight: 700, height: 24, bgcolor: alpha(pm.color, 0.1), color: pm.color, textTransform: 'capitalize',
                                  '& .MuiChip-icon': { color: pm.color } }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={r.status} size="small"
                                sx={{ fontWeight: 700, height: 24, bgcolor: sm.bg, color: sm.color, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem' }}>
                                {fullName(r.assignedTo) || r.assignedTo?.username || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem', color: r.dueDate && new Date(r.dueDate) < new Date() ? '#dc2626' : 'text.primary' }}>
                                {r.dueDate ? new Date(r.dueDate).toLocaleDateString() : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#10b981' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: 2 }}>
              <Assignment sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography sx={{ fontWeight: 700 }}>{editing ? 'Edit Task' : 'New Task'}</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Title *</Typography>
              <TextField fullWidth size="small" placeholder="Task title" value={form.title} onChange={e => F('title', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Description</Typography>
              <TextField fullWidth size="small" multiline rows={3} placeholder="Task details..." value={form.description} onChange={e => F('description', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Priority</Typography>
              <TextField fullWidth size="small" select value={form.priority} onChange={e => F('priority', e.target.value)}>
                {['low', 'medium', 'high', 'urgent'].map(p => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Status</Typography>
              <TextField fullWidth size="small" select value={form.status} onChange={e => F('status', e.target.value)}>
                {[{ v: 'pending', l: 'Pending' }, { v: 'in_progress', l: 'In Progress' }, { v: 'completed', l: 'Completed' }, { v: 'cancelled', l: 'Cancelled' }].map(s => <MenuItem key={s.v} value={s.v}>{s.l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Assigned To</Typography>
              <TextField fullWidth size="small" select value={form.assignedTo} onChange={e => F('assignedTo', e.target.value)}>
                <MenuItem value="">Unassigned</MenuItem>
                {users.map(u => <MenuItem key={u._id} value={u._id}>{fullName(u) || u.username}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Due Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.dueDate} onChange={e => F('dueDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700, px: 4 }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Task?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tasks;
